import { useCallback, useEffect, useMemo, useState } from "react";
import { ExternalLink, Loader2, MapPin, RefreshCw } from "lucide-react";

import { request } from "@/api/client";
import { createManualBookingLead } from "@/api/booking";
import { createLead } from "@/api/crm";
import { useToast } from "@/components/ToastProvider";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import type { VenueEvent, VenueRecord } from "@/types/venues";
import { normalizeUnknownValue } from "@/lib/utils";

type VenueDrawerProps = {
  venue: VenueRecord | null;
  open: boolean;
  onClose: () => void;
};

const toEventArray = (payload: unknown): VenueEvent[] => {
  if (Array.isArray(payload)) return payload as VenueEvent[];
  if (payload && typeof payload === "object" && Array.isArray((payload as { events?: VenueEvent[] }).events)) {
    return (payload as { events?: VenueEvent[] }).events ?? [];
  }
  return [];
};

const formatEventDate = (value?: string | null) => {
  if (!value) return "Date pending";
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const formatLocation = (venue: VenueRecord | null) => {
  if (!venue) return "";
  const city = normalizeUnknownValue(venue.city);
  const state = normalizeUnknownValue(venue.state);
  const country = normalizeUnknownValue(venue.country);
  return [city, state, country].filter(Boolean).join(", ");
};

export function VenueDrawer({ venue, open, onClose }: VenueDrawerProps) {
  const { push } = useToast();
  const [events, setEvents] = useState<VenueEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [leadBusy, setLeadBusy] = useState(false);

  const loadEvents = useCallback(() => {
    if (!venue?.id) return;
    setEvents([]);
    setEventsError(null);
    setEventsLoading(true);

    let cancelled = false;

    request(`/events/by-venue/${venue.id}`)
      .then((payload) => {
        if (cancelled) return;
        setEvents(toEventArray(payload));
      })
      .catch((error) => {
        if (cancelled) return;
        setEventsError(error instanceof Error ? error.message : "Unable to load events.");
      })
      .finally(() => {
        if (cancelled) return;
        setEventsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [venue?.id]);

  useEffect(() => {
    if (!open || !venue?.id) {
      setEvents([]);
      setEventsError(null);
      setEventsLoading(false);
      return () => {
        /* no-op */
      };
    }
    const cancel = loadEvents();

    return () => {
      cancel?.();
    };
  }, [loadEvents, open, venue?.id]);

  const upcomingHeader = useMemo(() => {
    if (!venue?.name) return "Upcoming shows";
    return `${normalizeUnknownValue(venue.name) ?? "Upcoming shows"}`;
  }, [venue?.name]);

  const handleSendToBookingLeads = async () => {
    if (!venue?.id) return;
    setLeadBusy(true);
    try {
      const contactId = typeof venue.id === "number" ? venue.id : Number(venue.id);
      if (Number.isNaN(contactId)) {
        throw new Error("Venue contact id is missing.");
      }
      const locationLabel = [venue.city, venue.state].filter(Boolean).join(", ");
      const leadTitle = `${normalizeUnknownValue(venue.name) ?? "Venue"}${locationLabel ? ` • ${locationLabel}` : ""}`;
      const leadRecord = await createLead({
        contact_id: contactId,
        title: leadTitle,
        source: "Venue Map",
        status: "open",
        pipeline_stage: "Prospecting",
        region: locationLabel || undefined,
        description: venue.tags?.length ? venue.tags.slice(0, 3).join(", ") : undefined,
        metadata: {
          venue_map_source: true,
          venue_capacity: venue.capacity ?? undefined,
        },
      });
      await createManualBookingLead({
        contact_id: contactId,
        crm_lead_id: leadRecord?.id,
        company_name: venue.company_name ?? venue.name ?? undefined,
        contact_name: venue.contact_name ?? venue.name ?? undefined,
        contact_email: venue.email ?? undefined,
        phone: venue.phone ?? undefined,
        city: venue.city ?? undefined,
        state: venue.state ?? undefined,
        country: venue.country ?? undefined,
        tags: venue.tags ?? undefined,
        pipeline_stage: "Prospecting",
        source: "Venue Map",
        metadata: {
          venue_map_source: true,
          venue_website: venue.website ?? undefined,
        },
      });
      push({
        title: "Booking lead created",
        message: `${normalizeUnknownValue(venue.name) ?? "Venue"} sent to booking leads.`,
      });
    } catch (error) {
      push({
        title: "Booking lead error",
        message: error instanceof Error ? error.message : "Unable to create booking lead.",
      });
    } finally {
      setLeadBusy(false);
    }
  };

  const crmHref = useMemo(() => {
    if (!venue?.id) return "/contacts";
    const params = new URLSearchParams({ contactId: String(venue.id) });
    return `/contacts?${params.toString()}`;
  }, [venue?.id]);

  const normalizedWebsite = useMemo(() => {
    if (!venue?.website) return null;
    if (/^https?:\/\//i.test(venue.website)) return venue.website;
    return `https://${venue.website}`;
  }, [venue?.website]);

  const location = formatLocation(venue);

  return (
    <Drawer open={open} onOpenChange={(next) => !next && onClose()}>
      <DrawerContent className="border border-border bg-background text-foreground">
        <DrawerHeader className="border-b border-border pb-4">
          <DrawerTitle className="text-xl font-semibold text-foreground">
            {normalizeUnknownValue(venue?.name) ?? "Venue details"}
          </DrawerTitle>
          <DrawerDescription className="flex items-center gap-2 text-muted-foreground">
            <MapPin className="h-4 w-4 text-amber-300" />
            {location || "Location pending"}
          </DrawerDescription>
        </DrawerHeader>

        <div className="flex flex-col gap-6 overflow-y-auto px-6 py-6 text-sm">
          <div className="rounded-lg border border-border/80 bg-background/30 p-4">
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">Venue ID</dt>
                <dd className="font-mono text-foreground">{venue?.id}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">Capacity</dt>
                <dd className="text-foreground">{venue?.capacity ? venue.capacity.toLocaleString() : "Unknown"}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">Latitude</dt>
                <dd className="text-foreground">
                  {typeof venue?.latitude === "number" ? venue.latitude.toFixed(4) : "Missing"}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">Longitude</dt>
                <dd className="text-foreground">
                  {typeof venue?.longitude === "number" ? venue.longitude.toFixed(4) : "Missing"}
                </dd>
              </div>
            </dl>
            {venue?.tags?.length ? (
              <div className="mt-4 flex flex-wrap gap-2">
                {venue.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="bg-muted text-primary-foreground dark:text-amber-200">
                    {tag}
                  </Badge>
                ))}
              </div>
            ) : null}
          </div>

          <div className="rounded-lg border border-border/80 bg-background/30 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-foreground">Contact info</h3>
                <p className="text-xs text-muted-foreground">Synced from CRM for quick outreach.</p>
              </div>
              <Button variant="outline" size="sm" asChild>
                <a href={crmHref} target="_blank" rel="noreferrer">
                  <ExternalLink className="h-4 w-4" />
                  Edit in CRM
                </a>
              </Button>
            </div>
            <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">Company</dt>
                <dd className="text-foreground">{venue?.company_name || "—"}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">Primary contact</dt>
                <dd className="text-foreground">{venue?.contact_name || venue?.name || "—"}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">Email</dt>
                <dd className="text-foreground">
                  {venue?.email ? (
                    <a href={`mailto:${venue.email}`} className="text-primary-foreground dark:text-amber-200 hover:underline">
                      {venue.email}
                    </a>
                  ) : (
                    "—"
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">Phone</dt>
                <dd className="text-foreground">
                  {venue?.phone ? (
                    <a href={`tel:${venue.phone}`} className="text-primary-foreground dark:text-amber-200 hover:underline">
                      {venue.phone}
                    </a>
                  ) : (
                    "—"
                  )}
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">Website</dt>
                <dd className="text-foreground">
                  {normalizedWebsite ? (
                    <a href={normalizedWebsite} target="_blank" rel="noreferrer" className="text-primary-foreground dark:text-amber-200 hover:underline">
                      {venue?.website}
                    </a>
                  ) : (
                    "—"
                  )}
                </dd>
              </div>
            </dl>
          </div>

          <section className="space-y-4">
            <header className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-foreground">{upcomingHeader}</h3>
                <p className="text-xs text-muted-foreground">Pulled live from upcoming CRM events.</p>
              </div>
              {eventsError ? (
                <Button variant="outline" size="sm" onClick={() => { loadEvents(); }}>
                  <RefreshCw className="h-4 w-4" />
                  Retry
                </Button>
              ) : null}
            </header>
            {eventsLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading upcoming shows…
              </div>
            ) : null}
            {!eventsLoading && eventsError ? (
              <p className="text-sm text-red-400">{eventsError}</p>
            ) : null}
            {!eventsLoading && !eventsError && events.length === 0 ? (
              <p className="text-sm text-muted-foreground">No upcoming shows recorded for this venue.</p>
            ) : null}

            <ul className="space-y-3">
              {events.map((event) => (
                <li
                  key={event.id}
                  className="rounded-lg border border-border bg-background/30 px-4 py-3"
                >
                  <div className="flex items-center justify-between text-sm font-medium text-foreground">
                    <span>{formatEventDate(event.event_date)}</span>
                    {event.status ? (
                      <span className="text-xs uppercase tracking-wide text-amber-300">
                        {event.status}
                      </span>
                    ) : null}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {[normalizeUnknownValue(event.city), normalizeUnknownValue(event.state)]
                      .filter(Boolean)
                      .join(", ")}
                  </div>
                  {event.artist_name ? (
                    <p className="mt-1 text-sm text-foreground/80">{event.artist_name}</p>
                  ) : null}
                  {event.notes ? (
                    <p className="mt-1 text-xs text-muted-foreground">{event.notes}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        </div>

        <DrawerFooter className="border-t border-border bg-background/70">
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button variant="secondary" asChild className="flex-1 bg-muted text-foreground hover:bg-muted">
              <a href={crmHref} target="_blank" rel="noreferrer">
                <ExternalLink className="h-4 w-4" />
                Open CRM Record
              </a>
            </Button>
            <Button
              variant="default"
              className="flex-1 bg-emerald-500 text-emerald-950 hover:bg-emerald-400"
              onClick={handleSendToBookingLeads}
              disabled={leadBusy}
            >
              {leadBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Send to Booking Leads
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
